import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import RecipientTable from './components/RecipientTable';
import EmailComposer from './components/EmailComposer';
import BatchConfig from './components/BatchConfig';
import SendingProgress from './components/SendingProgress';
import CampaignHistory from './components/CampaignHistory';
import EmailTesting from './components/EmailTesting';
import { getToken } from '../../../../../services/api';
import './EmailManagementMG.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const EmailManagementMG = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('compose'); // 'compose', 'history', or 'testing'
  
  // Recipients state
  const [recipients, setRecipients] = useState({
    cpf: [],
    cnpj: [],
    oab: []
  });
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [recipientSummary, setRecipientSummary] = useState(null);
  
  // Email composition state
  const [emailData, setEmailData] = useState({
    name: '',
    templateId: '',
    subject: '',
    customContent: '',
    batchConfig: {
      batchSize: 50,
      delaySeconds: 30
    }
  });
  
  // Campaign state
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [campaignStatus, setCampaignStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  
  // Track if initial load has been done to prevent duplicate requests
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const abortControllerRef = useRef(null);
  
  // Define all callback functions first
  const fetchRecipients = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/email/ba/recipients`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch recipients');
      }
      
      const data = await response.json();
      setRecipients(data.recipients);
      setRecipientSummary(data.summary);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching recipients:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/email/templates`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }
      
      const data = await response.json();
      setTemplates(data.templates);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  }, []);
  
  const fetchCampaignStatus = useCallback(async (campaignId) => {
    try {
      const response = await fetch(`${API_URL}/email/ba/campaign/${campaignId}/status`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch campaign status');
      }
      
      const data = await response.json();
      setCampaignStatus(data.campaign);
      
      // If campaign is completed or failed, stop polling
      if (data.campaign.status === 'completed' || data.campaign.status === 'failed') {
        setActiveCampaign(null);
      }
    } catch (err) {
      console.error('Error fetching campaign status:', err);
    }
  }, []);
  
  const handleRecipientSelection = useCallback((selected) => {
    setSelectedRecipients(selected);
  }, []);
  
  const handleEmailDataChange = (data) => {
    setEmailData(data);
  };
  
  const handleBatchConfigChange = (config) => {
    setEmailData(prev => ({
      ...prev,
      batchConfig: config
    }));
  };
  
  // Fetch recipients on mount - only once
  useEffect(() => {
    if (!initialLoadDone) {
      setInitialLoadDone(true);
      fetchRecipients();
      fetchTemplates();
    }
    
    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [initialLoadDone, fetchRecipients, fetchTemplates]);
  
  // Poll campaign status when active
  useEffect(() => {
    let interval;
    if (activeCampaign) {
      // Fetch immediately
      fetchCampaignStatus(activeCampaign._id);
      
      // Then poll every 2 seconds
      interval = setInterval(() => {
        fetchCampaignStatus(activeCampaign._id);
      }, 2000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [activeCampaign, fetchCampaignStatus]);
  
  const createCampaign = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/email/ba/campaign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          ...emailData,
          recipients: selectedRecipients
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create campaign');
      }
      
      const data = await response.json();
      return data.campaign;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const startCampaign = async (campaignId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/email/ba/campaign/${campaignId}/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to start campaign');
      }
      
      const data = await response.json();
      setActiveCampaign(data.campaign);
    } catch (err) {
      setError(err.message);
      console.error('Error starting campaign:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendEmails = async () => {
    if (selectedRecipients.length === 0) {
      setError('Por favor, selecione pelo menos um destinatário');
      return;
    }
    
    if (!emailData.templateId) {
      setError('Por favor, selecione um template de email');
      return;
    }
    
    if (!emailData.name) {
      setError('Por favor, forneça um nome para a campanha');
      return;
    }
    
    try {
      // Create campaign
      const campaign = await createCampaign();
      
      // Start sending
      await startCampaign(campaign._id);
      
    } catch (err) {
      console.error('Error sending emails:', err);
    }
  };
  
  const pauseCampaign = async () => {
    if (!activeCampaign) return;
    
    try {
      const response = await fetch(`${API_URL}/email/ba/campaign/${activeCampaign._id}/pause`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to pause campaign');
      }
      
      setActiveCampaign(null);
    } catch (err) {
      console.error('Error pausing campaign:', err);
    }
  };
  
  const resumeCampaign = async (campaignId) => {
    await startCampaign(campaignId);
  };
  
  return (
    <div className="email-management-ba-container">
      <div className="email-header">
        <div className="header-content">
          <h1>Enviar Emails - Minas Gerais</h1>
          <p className="subtitle">Gerencie o envio de emails para destinatários com precatórios na Minas Gerais</p>
        </div>
        <button className="back-button" onClick={() => navigate('/admin/dashboard')}>
          ← Voltar
        </button>
      </div>
      
      {error && (
        <div className="error-message">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      
      <div className="tabs-container">
        <div className="tabs-nav">
          <button 
            className={`tab-button ${activeTab === 'compose' ? 'active' : ''}`}
            onClick={() => setActiveTab('compose')}
          >
            Nova Campanha
          </button>
          <button 
            className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Histórico de Campanhas
          </button>
          <button 
            className={`tab-button ${activeTab === 'testing' ? 'active' : ''}`}
            onClick={() => setActiveTab('testing')}
          >
            🧪 Testar Email
          </button>
          <button 
            className="tab-button analytics-button"
            onClick={() => navigate('/admin/email/analytics')}
          >
            📊 Analytics
          </button>
        </div>
      </div>
      
      {activeTab === 'compose' ? (
        <>
          {activeCampaign ? (
            <SendingProgress 
              campaign={activeCampaign}
              status={campaignStatus}
              onPause={pauseCampaign}
              onResume={resumeCampaign}
            />
          ) : (
            <>
              {recipientSummary && (
                <div className="summary-card">
                  <h3>Resumo dos Destinatários</h3>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span className="label">Total de Precatórios:</span>
                      <span className="value">{recipientSummary.totalPrecatorios}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Com JurisConsult:</span>
                      <span className="value">{recipientSummary.precatoriosWithJurisConsult}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Total de Destinatários:</span>
                      <span className="value">{recipientSummary.totalRecipients}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Com Email:</span>
                      <span className="value">{recipientSummary.recipientsWithEmail}</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="email-composition-grid">
                <div className="left-panel">
                  <RecipientTable 
                    recipients={recipients}
                    onSelectionChange={handleRecipientSelection}
                    loading={loading}
                  />
                </div>
                
                <div className="right-panel">
                  <EmailComposer 
                    templates={templates}
                    onChange={handleEmailDataChange}
                    selectedCount={selectedRecipients.length}
                  />
                  
                  <BatchConfig 
                    onChange={handleBatchConfigChange}
                    recipientCount={selectedRecipients.length}
                  />
                  
                  <div className="action-buttons">
                    <button 
                      className="send-button"
                      onClick={handleSendEmails}
                      disabled={loading || selectedRecipients.length === 0}
                    >
                      {loading ? 'Processando...' : `Enviar para ${selectedRecipients.length} destinatário(s)`}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      ) : activeTab === 'history' ? (
        <CampaignHistory state="MG" />
      ) : (
        <EmailTesting templates={templates} />
      )}
    </div>
  );
};

export default EmailManagementMG;